import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, ArrowRight } from "lucide-react";
import { home } from "../mock";
import Reveal from "../components/Reveal";

function scrollToId(id) {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

const GoldRule = () => <div className="gold-rule my-0" />;

/* Full-height half image + text (image fills its half edge-to-edge) */
function SplitSection({ eyebrow, heading, body, img, reverse, bg = "bg-[#0a0908]", soft = false }) {
  return (
    <section className={bg}>
      <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
        <Reveal className={`${reverse ? "lg:order-2" : ""}`}>
          <div className="relative w-full h-full min-h-[360px] lg:min-h-[560px] overflow-hidden group">
            <img src={img} alt={heading} className="absolute inset-0 w-full h-full object-cover bw transition-transform duration-[1400ms] group-hover:scale-105" />
          </div>
        </Reveal>
        <Reveal delay={120} className={reverse ? "lg:order-1" : ""}>
          <div className="h-full flex flex-col justify-center px-8 md:px-16 py-16">
            <p className="eyebrow mb-5">{eyebrow}</p>
            {soft ? (
              <h2 className="font-serif-accent normal-case text-3xl md:text-5xl text-white leading-snug" style={{ letterSpacing: "0.005em" }}>{heading}</h2>
            ) : (
              <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight">{heading}</h2>
            )}
            {body && <p className="font-body text-white/60 mt-6 text-lg leading-relaxed font-light">{body}</p>}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ImageBand({ bg, children, minH = "min-h-[70vh]", tint = "black" }) {
  const overlay = tint === "purple" ? "bg-[hsl(var(--melano-purple))]/85" : "bg-[#0a0908]/72";
  return (
    <section className={`relative ${minH} flex items-center overflow-hidden`}>
      <div className="absolute inset-0 bg-fixed bg-center bg-cover bw" style={{ backgroundImage: `url(${bg})` }} />
      <div className={`absolute inset-0 ${overlay}`} />
      <div className="grain" />
      <div className="container-x relative z-10 text-center">{children}</div>
    </section>
  );
}

export function BrandCard({ brand }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`flip-card w-full aspect-square cursor-pointer ${flipped ? "is-flipped" : ""}`}
      onClick={() => setFlipped((v) => !v)}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") setFlipped((v) => !v); }}
      aria-label={`${brand.name} — ${brand.caption}`}
    >
      <div className="flip-inner">
        <div className="flip-face flip-front bg-[#050505] border border-gold/30 rounded-2xl overflow-hidden items-center justify-center">
          <div className="absolute right-1 top-1/2 -translate-y-1/2 h-44 w-44 icon-gold opacity-[0.06]" style={{ WebkitMaskImage: `url(${brand.icon})`, maskImage: `url(${brand.icon})` }} aria-hidden="true" />
          <div className="relative flex-1 w-full flex items-center justify-center">
            <div className="w-32 h-32 rounded-full border border-gold/60 flex items-center justify-center">
              <div className="h-20 w-20 icon-gold" style={{ WebkitMaskImage: `url(${brand.icon})`, maskImage: `url(${brand.icon})` }} aria-label={brand.name} role="img" />
            </div>
          </div>
          <div className="absolute left-4 right-4 bottom-5 bg-[#f3efe6] rounded-md py-2 px-3">
            <p className="font-display uppercase font-bold tracking-[0.22em] text-[13px] text-[#8a6f3d] text-center">{brand.name}</p>
          </div>
        </div>
        <div className="flip-face flip-back bg-[#0a0908] border border-white/40 rounded-2xl items-center justify-center text-center p-8">
          <img src={brand.icon} alt="" aria-hidden="true" className="h-12 w-12 object-contain mb-6" style={{ filter: "brightness(0) invert(1)" }} />
          <p className="font-display uppercase font-bold tracking-[0.14em] text-white text-sm mb-4">{brand.name}</p>
          <p className="font-body text-white/80 text-base leading-snug">{brand.caption}</p>
          {brand.link ? (
            <a href={brand.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-6 inline-flex items-center gap-1 text-white font-display uppercase tracking-[0.16em] text-xs link-underline">
              Visit site <ArrowUpRight size={14} />
            </a>
          ) : (
            <span className="mt-6 inline-flex items-center gap-1 text-white/60 font-display uppercase tracking-[0.16em] text-xs">Coming soon</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { hero, thoughtLeadership, whatHappened, inventory, philosophy, whyWeNeed, whatWeDo, landLineage, ecosystem, quote, workWith, closing } = home;

  return (
    <div>
      {/* 1 — HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-center bg-cover bw" style={{ backgroundImage: `url(${hero.bg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/85 via-[#0a0908]/78 to-[#0a0908]" />
        <div className="grain" />
        <div className="relative z-10 flex flex-col items-center text-center container-x">
          <img src="/assets/melano/melano_wordmark.png" alt="Melano International monogram" className="w-24 md:w-32 h-auto floaty mb-8" />
          <p className="font-display uppercase font-bold tracking-[0.42em] gold-gradient-text-brand text-sm md:text-base mb-5">{hero.wordmark}</p>
          <p className="font-body text-white/55 text-sm md:text-base leading-relaxed font-light max-w-2xl mx-auto mb-6">{hero.small}</p>
          <h1 className="font-display normal-case text-white max-w-6xl leading-[1.12] pb-2">
            <span className="block font-light text-white/70 mb-1" style={{ fontSize: "clamp(1.1rem, 3.2vw, 2.25rem)" }}>Cultivating</span>
            <span className="block font-medium gold-gradient-text-brand" style={{ fontSize: "clamp(2rem, 8vw, 5rem)", lineHeight: 1.14, paddingBottom: "0.1em" }}>Intergenerational</span>
            <span className="block font-medium gold-gradient-text-brand" style={{ fontSize: "clamp(2rem, 8vw, 5rem)", lineHeight: 1.14 }}>Vision</span>
          </h1>
          <button onClick={() => scrollToId(hero.cta.target)} className="gold-lift mt-10 inline-flex items-center gap-2 border border-gold/60 text-gold font-display uppercase font-bold tracking-[0.18em] text-sm px-8 py-4 rounded-sm hover:gold-gradient-bg hover:text-[#0a0908] transition-colors">
            {hero.cta.label}
          </button>
        </div>
        <button onClick={() => scrollToId("#thought-leadership")} className="absolute bottom-8 z-10 text-gold/70 hover:text-gold transition-colors" aria-label="Scroll down">
          <ChevronDown className="animate-bounce" size={30} />
        </button>
      </section>
      <GoldRule />

      {/* 2 — THOUGHT LEADERSHIP (clean B&W / white splice) */}
      <section id="thought-leadership">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <Reveal>
            <div className="relative h-[360px] lg:h-[560px] overflow-hidden group">
              <img src={thoughtLeadership.img} alt={thoughtLeadership.eyebrow} className="w-full h-full object-cover bw transition-transform duration-[1400ms] group-hover:scale-105" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="bg-white h-full flex flex-col justify-center px-8 md:px-16 py-16">
              <p className="font-display uppercase font-bold tracking-[0.3em] text-[10px] text-[#8a6f3d] mb-6">{thoughtLeadership.eyebrow}</p>
              <p className="font-display font-bold uppercase text-3xl md:text-5xl text-[#0a0908] leading-tight">{thoughtLeadership.quote}</p>
            </div>
          </Reveal>
        </div>
      </section>
      <GoldRule />

      {/* 3 — WHAT HAPPENED TO VISION? + OUR INVENTORY (purple, typographic, no photo) */}
      <section className="bg-melano-purple relative overflow-hidden section-pad">
        <div className="grain" />
        <div className="container-x relative z-10 max-w-5xl mx-auto">
          <Reveal><p className="font-display uppercase font-bold tracking-[0.32em] text-[11px] text-gold mb-8 text-center">{whatHappened.eyebrow}</p></Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl lg:text-6xl text-white leading-tight text-center">{whatHappened.body}</h2>
          </Reveal>

          <Reveal delay={160}>
            <div className="gold-rule my-14 md:my-20 max-w-xl mx-auto" />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-12 items-start">
            <Reveal>
              <p className="font-display uppercase font-bold tracking-[0.3em] text-[11px] text-gold md:pt-3">{inventory.eyebrow}</p>
            </Reveal>
            <Reveal delay={120}>
              <p className="font-serif-accent italic text-2xl md:text-4xl text-white/90 leading-snug">{inventory.body}</p>
            </Reveal>
          </div>
        </div>
      </section>
      <GoldRule />

      {/* 4 — PHILOSOPHY: TIMELESS VISION (white block, full-half image) */}
      <section className="bg-white text-[#0a0908]">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
          <Reveal>
            <div className="relative w-full h-full min-h-[380px] lg:min-h-[600px] overflow-hidden">
              <img src={philosophy.img} alt={philosophy.title} className="absolute inset-0 w-full h-full object-cover bw" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full flex flex-col justify-center px-8 md:px-16 py-16">
              <p className="font-display uppercase font-bold tracking-[0.32em] text-[10px] text-[#8a6f3d] mb-4">{philosophy.eyebrow}</p>
              <h2 className="font-display uppercase font-bold text-4xl md:text-6xl text-[#0a0908] leading-none">{philosophy.title}</h2>
              <p className="font-serif-accent italic text-xl md:text-2xl text-[#4a3f2a] mt-5">{philosophy.lead}</p>
              <p className="font-body text-[#3a3530] mt-4 text-lg leading-relaxed font-light">{philosophy.body}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5a — WHY WE NEED IT */}
      <SplitSection eyebrow={whyWeNeed.eyebrow} heading={whyWeNeed.body} img={whyWeNeed.img} bg="bg-[#0a0908]" soft />
      <GoldRule />
      {/* 5b — WHAT WE DO (full-width band, no photo) */}
      <section className="section-pad bg-[#0d0b09]">
        <div className="container-x max-w-4xl mx-auto text-center">
          <Reveal><p className="eyebrow mb-6">{whatWeDo.eyebrow}</p></Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif-accent normal-case text-4xl md:text-6xl text-white leading-snug">{whatWeDo.body}</h2>
          </Reveal>
        </div>
      </section>
      <GoldRule />

      {/* 6 — LAND & LINEAGE (retained) */}
      <ImageBand bg={landLineage.bg}>
        <Reveal><p className="eyebrow mb-6">{landLineage.eyebrow}</p></Reveal>
        <Reveal delay={100}>
          <h2 className="font-display uppercase font-bold text-4xl md:text-6xl text-white max-w-4xl mx-auto leading-tight">
            {landLineage.headingLines.map((line) => (
              <span key={line} className="block">{line}</span>
            ))}
          </h2>
        </Reveal>
        <Reveal delay={180}><p className="font-serif-accent italic text-xl md:text-2xl text-white/85 mt-8 max-w-2xl mx-auto leading-relaxed">{landLineage.body}</p></Reveal>
      </ImageBand>

      {/* 7 — FOUR BRANDS */}
      <section id="ecosystem" className="section-pad bg-[#0a0908]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="eyebrow mb-5">{ecosystem.eyebrow}</p></Reveal>
            <Reveal delay={80}><h2 className="font-display uppercase font-bold text-4xl md:text-6xl text-white">{ecosystem.heading}</h2></Reveal>
            <Reveal delay={160}><p className="text-white/55 mt-5">{ecosystem.note}</p></Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosystem.brands.map((b, i) => (<Reveal key={b.id} delay={i * 120}><BrandCard brand={b} /></Reveal>))}
          </div>
          <Reveal delay={200}><p className="text-center text-white/40 text-xs uppercase tracking-[0.2em] font-display mt-10">Tap a tile to reveal</p></Reveal>
        </div>
      </section>

      {/* 8 — TIMELESS VISION QUOTE */}
      <ImageBand bg={quote.bg} minH="min-h-[60vh]">
        <Reveal><p className="font-serif-accent italic text-3xl md:text-5xl leading-snug text-white max-w-4xl mx-auto">&ldquo;{quote.text}&rdquo;</p></Reveal>
        <Reveal delay={120}><p className="text-white/60 mt-8 max-w-xl mx-auto">{quote.caption}</p></Reveal>
      </ImageBand>

      {/* 9 — WHO DO WE WORK WITH (clean purple band) */}
      <section className="section-pad bg-melano-purple relative overflow-hidden">
        <div className="grain" />
        <div className="container-x relative z-10 text-center max-w-4xl mx-auto">
          <Reveal><p className="font-display uppercase font-bold tracking-[0.3em] text-[11px] text-gold mb-6">{workWith.eyebrow}</p></Reveal>
          <Reveal delay={100}><h2 className="font-display uppercase font-bold text-3xl md:text-5xl text-white leading-tight">{workWith.body}</h2></Reveal>
        </div>
      </section>

      {/* 10 — CLOSING CTA (strong black & white) */}
      <section className="relative section-pad overflow-hidden bg-[#050505]">
        <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${closing.bg})`, filter: "grayscale(100%) contrast(1.35) brightness(0.75)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/85" />
        <div className="grain" />
        <div className="container-x text-center relative z-10 py-10">
          <Reveal><img src="/assets/melano/melano_wordmark.png" alt="" className="w-16 h-auto mx-auto mb-8 opacity-90" /></Reveal>
          <Reveal delay={60}><h2 className="font-display uppercase font-bold text-4xl md:text-6xl lg:text-7xl text-white">{closing.heading}</h2></Reveal>
          <Reveal delay={110}><p className="font-serif-accent italic text-2xl md:text-3xl text-gold mt-5">{closing.lead}</p></Reveal>
          <Reveal delay={150}><p className="text-white/75 mt-5 max-w-xl mx-auto">{closing.body}</p></Reveal>
          <Reveal delay={200}>
            <Link to={closing.cta.to} className="gold-lift mt-10 inline-flex items-center gap-2 gold-gradient-bg text-[#0a0908] font-display uppercase font-bold tracking-[0.18em] text-sm px-9 py-4 rounded-sm hover:brightness-110 transition">
              {closing.cta.label} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
